import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './auth-context';

interface PWAContextType {
  deferredPrompt: any;
  isInstallable: boolean;
  installApp: () => Promise<void>;
  showInstallPopup: boolean;
  dismissPopup: () => void;
}

const PWAContext = createContext<PWAContextType | undefined>(undefined);

export const PWAProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [showInstallPopup, setShowInstallPopup] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: any) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e);
      setIsInstallable(true);
      
      // Check if we should show the one-time popup for logged in users
      const hasSeenPopup = localStorage.getItem('pwa_popup_shown');
      if (user && !hasSeenPopup) {
        setShowInstallPopup(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [user]);

  const installApp = async () => {
    if (!deferredPrompt) return;
    
    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }
    
    // We've used the prompt, and can't use it again
    setDeferredPrompt(null);
    setIsInstallable(false);
    setShowInstallPopup(false);
    localStorage.setItem('pwa_popup_shown', 'true');
  };

  const dismissPopup = () => {
    setShowInstallPopup(false);
    localStorage.setItem('pwa_popup_shown', 'true');
  };

  return (
    <PWAContext.Provider value={{ deferredPrompt, isInstallable, installApp, showInstallPopup, dismissPopup }}>
      {children}
    </PWAContext.Provider>
  );
};

export const usePWA = () => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};
